import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiDownloadCloud, FiRefreshCw, FiCheckCircle, FiInfo, FiLayers, FiCalendar } from 'react-icons/fi';
import Modal from '../../../../components/Modals/Modal';
import useToast from '../../../../hooks/useToast';
import { useSyncTceConferencia } from '../../../../services/conferencia.service';

export interface SyncTceModalHandle {
  openSyncModal: (params?: { municipio?: string; ano?: string; mes?: string; tipoProcesso?: 'cont' | 'lic' }) => void;
}

interface SyncTceModalProps {
  municipios: { codigo?: string; nome: string; anos: string[] }[];
  onSuccess?: () => void;
}

type ModalRef = React.ElementRef<typeof Modal>;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #1e293b;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const InfoBox = styled.div`
  display: flex;
  gap: 12px;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  color: #1e40af;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    font-size: 18px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12.5px;
    font-weight: 700;
    color: #334155;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  select,
  input[type='text'],
  input[type='number'] {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 13.5px;
    color: #0f172a;
    background-color: #ffffff;
    outline: none;
    transition: border-color 0.15s ease-in-out;

    &:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
  }

  .help-text {
    font-size: 11.5px;
    color: #64748b;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;

  .title {
    font-size: 12px;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 4px;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  cursor: pointer;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    accent-color: #2563eb;
    cursor: pointer;
  }
`;

const FooterButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
  padding-top: 14px;
  border-top: 1px solid #e2e8f0;
`;

const ActionButton = styled.button<{ variant?: 'cancel' | 'primary' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: none;

  ${({ variant }) =>
    variant === 'primary'
      ? `
    background-color: #2563eb;
    color: #ffffff;
    box-shadow: 0 1px 2px rgba(37, 99, 235, 0.2);

    &:hover:not(:disabled) {
      background-color: #1d4ed8;
    }

    &:disabled {
      background-color: #93c5fd;
      cursor: not-allowed;
    }
  `
      : `
    background-color: #f1f5f9;
    color: #475569;
    border: 1px solid #cbd5e1;

    &:hover:not(:disabled) {
      background-color: #e2e8f0;
      color: #0f172a;
    }
  `}

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const SyncTceModal: React.ForwardRefRenderFunction<SyncTceModalHandle, SyncTceModalProps> = (
  { municipios, onSuccess },
  ref
) => {
  const modalRef = useRef<ModalRef>(null);
  const toast = useToast();

  const [municipio, setMunicipio] = useState('');
  const [exercicio, setExercicio] = useState<number>(new Date().getFullYear());
  const [periodoModo, setPeriodoModo] = useState<'ano' | 'mes' | 'intervalo'>('ano');
  const [mesSelecionado, setMesSelecionado] = useState<number>(1);
  const [mesInicio, setMesInicio] = useState<number>(1);
  const [mesFim, setMesFim] = useState<number>(12);
  const [syncDespesas, setSyncDespesas] = useState(true);
  const [syncLicitacoes, setSyncLicitacoes] = useState(true);
  const [anosEmpenhos, setAnosEmpenhos] = useState('');

  const syncMutation = useSyncTceConferencia();

  React.useImperativeHandle(ref, () => ({
    openSyncModal: (params) => {
      if (params?.municipio) {
        setMunicipio(params.municipio);
      } else if (municipios.length > 0 && !municipio) {
        setMunicipio(municipios[0].nome);
      }

      if (params?.ano) {
        const anoNum = parseInt(params.ano, 10);
        if (!isNaN(anoNum)) setExercicio(anoNum);
      }

      if (params?.tipoProcesso) {
        if (params.tipoProcesso === 'cont') {
          setSyncDespesas(true);
          setSyncLicitacoes(false);
        } else if (params.tipoProcesso === 'lic') {
          setSyncDespesas(false);
          setSyncLicitacoes(true);
        }
      }

      if (params?.mes && params.mes !== 'ALL') {
        const m = parseInt(params.mes, 10);
        if (!isNaN(m) && m >= 1 && m <= 12) {
          setPeriodoModo('mes');
          setMesSelecionado(m);
        }
      } else {
        setPeriodoModo('ano');
      }

      modalRef.current?.openModal();
    },
  }));

  const handleSync = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!municipio) {
      toast.errorToast('Selecione um município.');
      return;
    }

    const tipos: string[] = [];
    if (syncDespesas) tipos.push('DESPESAS');
    if (syncLicitacoes) tipos.push('LICITACOES');

    if (tipos.length === 0) {
      toast.errorToast('Selecione ao menos um tipo de dado (Despesas ou Licitações).');
      return;
    }

    let mIni = 1;
    let mFim = 12;
    if (periodoModo === 'mes') {
      mIni = mesSelecionado;
      mFim = mesSelecionado;
    } else if (periodoModo === 'intervalo') {
      mIni = mesInicio;
      mFim = mesFim;
    }

    // Processar anos adicionais de empenhos
    const anosEmpArr: number[] = [];
    if (anosEmpenhos.trim()) {
      anosEmpenhos
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n >= 2000 && n <= 2100)
        .forEach((n) => {
          if (!anosEmpArr.includes(n)) anosEmpArr.push(n);
        });
    }

    try {
      const response = await syncMutation.mutateAsync({
        municipio,
        exercicio: Number(exercicio),
        mesInicio: mIni,
        mesFim: mFim,
        tipos,
        anosEmpenhos: anosEmpArr.length > 0 ? anosEmpArr : undefined,
      });

      const stats = response?.stats;
      const total = stats?.totalInseridos || 0;
      const np = stats?.notasPagamentos || 0;
      const ne = stats?.notasEmpenhos || 0;
      const lic = stats?.licitacoes || 0;

      toast.successToast(
        `Sincronização com TCE-CE concluída! ${total} registros importados (NP: ${np}, NE: ${ne}, LIC: ${lic}).`
      );

      modalRef.current?.closeModal();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Erro ao sincronizar com o TCE-CE.';
      toast.errorToast(msg);
    }
  };

  return (
    <Modal ref={modalRef} title="Sincronizar Dados do Tribunal (TCE-CE / SIM)" maxWidth={620}>
      <form onSubmit={handleSync}>
        <Container>
          <InfoBox>
            <FiInfo />
            <div>
              <strong>Importação direta do TCE-CE:</strong> Esta ação se conecta à API oficial de Dados
              Abertos do Tribunal de Contas do Ceará, baixa os dados de empenhos, pagamentos (NP), despesas
              extraorçamentárias (XD) e licitações (LIC), e atualiza a conferência instantaneamente.
            </div>
          </InfoBox>

          <Row>
            <FormGroup>
              <label>
                <FiLayers /> Município
              </label>
              <select value={municipio} onChange={(e) => setMunicipio(e.target.value)}>
                {municipios.map((m) => (
                  <option key={m.nome} value={m.nome}>
                    {m.nome} {m.codigo ? `(${m.codigo})` : ''}
                  </option>
                ))}
              </select>
            </FormGroup>

            <FormGroup>
              <label>
                <FiCalendar /> Exercício / Ano
              </label>
              <input
                type="number"
                min={2010}
                max={2030}
                value={exercicio}
                onChange={(e) => setExercicio(parseInt(e.target.value, 10))}
                required
              />
            </FormGroup>
          </Row>

          <FormGroup>
            <label>Período de Referência</label>
            <Row style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 10px',
                  borderRadius: 6,
                  backgroundColor: periodoModo === 'ano' ? '#eff6ff' : '#f8fafc',
                  border: `1px solid ${periodoModo === 'ano' ? '#3b82f6' : '#cbd5e1'}`,
                  cursor: 'pointer',
                  fontSize: 12.5,
                  fontWeight: periodoModo === 'ano' ? 700 : 500,
                  color: periodoModo === 'ano' ? '#1d4ed8' : '#334155',
                }}
              >
                <input
                  type="radio"
                  name="periodoModo"
                  value="ano"
                  checked={periodoModo === 'ano'}
                  onChange={() => setPeriodoModo('ano')}
                />
                Ano Completo (01 a 12)
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 10px',
                  borderRadius: 6,
                  backgroundColor: periodoModo === 'mes' ? '#eff6ff' : '#f8fafc',
                  border: `1px solid ${periodoModo === 'mes' ? '#3b82f6' : '#cbd5e1'}`,
                  cursor: 'pointer',
                  fontSize: 12.5,
                  fontWeight: periodoModo === 'mes' ? 700 : 500,
                  color: periodoModo === 'mes' ? '#1d4ed8' : '#334155',
                }}
              >
                <input
                  type="radio"
                  name="periodoModo"
                  value="mes"
                  checked={periodoModo === 'mes'}
                  onChange={() => setPeriodoModo('mes')}
                />
                Mês Específico
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 10px',
                  borderRadius: 6,
                  backgroundColor: periodoModo === 'intervalo' ? '#eff6ff' : '#f8fafc',
                  border: `1px solid ${periodoModo === 'intervalo' ? '#3b82f6' : '#cbd5e1'}`,
                  cursor: 'pointer',
                  fontSize: 12.5,
                  fontWeight: periodoModo === 'intervalo' ? 700 : 500,
                  color: periodoModo === 'intervalo' ? '#1d4ed8' : '#334155',
                }}
              >
                <input
                  type="radio"
                  name="periodoModo"
                  value="intervalo"
                  checked={periodoModo === 'intervalo'}
                  onChange={() => setPeriodoModo('intervalo')}
                />
                Intervalo de Meses
              </label>
            </Row>

            {periodoModo === 'mes' && (
              <div style={{ marginTop: 8 }}>
                <select
                  value={mesSelecionado}
                  onChange={(e) => setMesSelecionado(parseInt(e.target.value, 10))}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      Mês {String(m).padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {periodoModo === 'intervalo' && (
              <Row style={{ marginTop: 8 }}>
                <FormGroup>
                  <label style={{ fontSize: 11 }}>Mês Início</label>
                  <select value={mesInicio} onChange={(e) => setMesInicio(parseInt(e.target.value, 10))}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        {String(m).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label style={{ fontSize: 11 }}>Mês Fim</label>
                  <select value={mesFim} onChange={(e) => setMesFim(parseInt(e.target.value, 10))}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        {String(m).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </FormGroup>
              </Row>
            )}
          </FormGroup>

          <CheckboxGroup>
            <div className="title">Tipos de Registros para Sincronizar</div>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={syncDespesas}
                onChange={(e) => setSyncDespesas(e.target.checked)}
              />
              <span>
                <strong>Processo Contábil</strong> (Pagamentos NP, Despesas Extras XD e Empenhos NE)
              </span>
            </CheckboxLabel>

            <CheckboxLabel>
              <input
                type="checkbox"
                checked={syncLicitacoes}
                onChange={(e) => setSyncLicitacoes(e.target.checked)}
              />
              <span>
                <strong>Processo Licitatório</strong> (Licitações LIC e Fornecedores)
              </span>
            </CheckboxLabel>
          </CheckboxGroup>

          <FormGroup>
            <label>Anos Adicionais para Empenhos (Restos a Pagar)</label>
            <input
              type="text"
              placeholder={`Ex: ${exercicio - 1}, ${exercicio - 2} (opcional)`}
              value={anosEmpenhos}
              onChange={(e) => setAnosEmpenhos(e.target.value)}
            />
            <span className="help-text">
              Garante o preenchimento de credor e objeto em pagamentos cujos empenhos foram emitidos em anos anteriores.
            </span>
          </FormGroup>

          <FooterButtons>
            <ActionButton
              type="button"
              variant="cancel"
              disabled={syncMutation.isLoading}
              onClick={() => modalRef.current?.closeModal()}
            >
              Cancelar
            </ActionButton>

            <ActionButton type="submit" variant="primary" disabled={syncMutation.isLoading}>
              {syncMutation.isLoading ? (
                <>
                  <FiRefreshCw className="spin" />
                  <span>Sincronizando com TCE-CE...</span>
                </>
              ) : (
                <>
                  <FiDownloadCloud />
                  <span>Iniciar Sincronização</span>
                </>
              )}
            </ActionButton>
          </FooterButtons>
        </Container>
      </form>
    </Modal>
  );
};

export default React.forwardRef(SyncTceModal);
